import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Input,
  Label,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import useAuth from 'hooks/useAuth';
import useTheme from 'hooks/useTheme';
import dayjs from 'dayjs';
import { dashboardService } from '../../../../services/dashboardService';
import { getOperationTimezone } from '../../../../utils/timezone';
import { apiErrorMessage } from '../../../../utils/apiErrorMessage';
import { MetricSkeleton, ErrorState, KpiCard, SortableTH } from '../../../../components/dashboard/shared/DashboardShared';
import DateRangeFilter from '../../../../components/dateRangeFilter/DateRangeFilter';
import { Table as UiTable, THead, TBody, TR, TH, TD } from '../../../../components/ui/Table';
import { Badge } from '../../../../components/ui/Badge';

const COLUMN_COLORS = {
  managed: '#10B981',
  pending: '#EF4444',
  total:   '#3B82F6',
};

const GROUP_OPTIONS = [
  { labelKey: 'Todos',      value: 'all'      },
  { labelKey: 'Top 5',     value: 'top5'     },
  { labelKey: 'Top 10',    value: 'top10'    },
  { labelKey: 'Top 20',    value: 'top20'    },
  { labelKey: 'Bottom 5',  value: 'bottom5'  },
  { labelKey: 'Bottom 10', value: 'bottom10' },
  { labelKey: 'Bottom 20', value: 'bottom20' },
];

// Presets del DateRangeFilter canónico. Incluye 'thisWeek' (opt-in) para no
// perder el 'Esta semana' que tenía el bloque propio anterior.
const CV_PRESETS = [
  { key: 'thisWeek', quick: true },
  { key: '7d', quick: true },
  { key: '30d', quick: true },
  { key: 'thisMonth', quick: true },
];

const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const managed = payload.find(p => p.dataKey === 'managed');
  const pending = payload.find(p => p.dataKey === 'pending');
  const total = (managed?.value ?? 0) + (pending?.value ?? 0);
  const pct = total > 0 ? ((managed?.value ?? 0) / total * 100).toFixed(1) : '0.0';
  return (
    <div className="cv-tooltip">
      <p className="cv-tooltip__label">{label}</p>
      <p className="cv-tooltip__row">
        <span className="cv-tooltip__dot" style={{ background: COLUMN_COLORS.managed }} />
        {t('Gestionados')}: <strong>{(managed?.value ?? 0).toLocaleString()}</strong>
      </p>
      <p className="cv-tooltip__row">
        <span className="cv-tooltip__dot" style={{ background: COLUMN_COLORS.pending }} />
        {t('Pendientes')}: <strong>{(pending?.value ?? 0).toLocaleString()}</strong>
      </p>
      <p className="cv-tooltip__row">
        {t('Total')}: <strong>{total.toLocaleString()}</strong> &mdash; % {t('gestión')}: <strong>{pct}%</strong>
      </p>
    </div>
  );
};

const [SORT_KEY_DEFAULT, SORT_DIR_DEFAULT] = ['total', 'desc'];

const ClientVolumeTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const logisticCompanyId = useMemo(() => user?.logisticCompany?._id || null, [user]);
  const dashboardIanaTz = useMemo(() => getOperationTimezone(user), [user]);

  const tickColor = isDark ? '#D1D5DB' : '#6B7280';
  const gridColor = isDark ? '#374151' : '#E5E7EB';

  const [startDate,    setStartDate]    = useState(() => dayjs().subtract(29, 'day').format('YYYY-MM-DD'));
  const [endDate,      setEndDate]      = useState(() => dayjs().format('YYYY-MM-DD'));
  const [activePreset, setActivePreset] = useState('30d'); // key del DateRangeFilter (== rango default 30 días)
  const [group,        setGroup]        = useState('top10');
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [sortKey,   setSortKey]   = useState(SORT_KEY_DEFAULT);
  const [sortDir,   setSortDir]   = useState(SORT_DIR_DEFAULT);

  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!logisticCompanyId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await dashboardService.getClientVolume(logisticCompanyId, startDate, endDate, dashboardIanaTz);
      setData(res.data);
      setHasLoaded(true);
    } catch (err) {
      // Object, not the string itself: apiErrorMessage() can return null and the
      // state doubles as the "there is an error" flag.
      setFetchError({ message: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [logisticCompanyId, startDate, endDate, dashboardIanaTz]);

  const filteredClients = useMemo(() => {
    if (!data?.clients) return [];
    if (group.startsWith('top')) {
      const n = parseInt(group.replace('top', ''), 10);
      return [...data.clients].sort((a, b) => b.total - a.total).slice(0, n);
    }
    if (group.startsWith('bottom')) {
      const n = parseInt(group.replace('bottom', ''), 10);
      return [...data.clients].sort((a, b) => a.total - b.total).slice(0, n);
    }
    // 'all' — respeta el sortKey/sortDir de la tabla
    return [...data.clients].sort((a, b) => {
      const mul = sortDir === 'asc' ? 1 : -1;
      if (typeof a[sortKey] === 'string') return mul * a[sortKey].localeCompare(b[sortKey]);
      return mul * (a[sortKey] - b[sortKey]);
    });
  }, [data, group, sortKey, sortDir]);

  const chartData = useMemo(() => {
    const ordered = group.startsWith('bottom')
      ? [...filteredClients].sort((a, b) => a.total - b.total)
      : [...filteredClients].sort((a, b) => b.total - a.total);
    return ordered.map(c => ({
      name:     c.businessName.length > 28 ? c.businessName.slice(0, 26) + '…' : c.businessName,
      fullName: c.businessName,
      managed:  c.managed,
      pending:  c.pending,
    }));
  }, [filteredClients, group]);

  const totals = useMemo(() => data?.totals ?? { total: 0, managed: 0, pending: 0 }, [data]);
  const managedPct = totals.total > 0 ? ((totals.managed / totals.total) * 100).toFixed(1) : '0.0';
  const pendingPct = totals.total > 0 ? ((totals.pending / totals.total) * 100).toFixed(1) : '0.0';

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Altura dinámica del gráfico: 38 px por cliente, mínimo 260 px
  const chartHeight = Math.max(260, chartData.length * 38);

  // Ancho dinámico del eje Y según la longitud del nombre más largo
  const yAxisWidth = useMemo(() => {
    if (!chartData.length) return 160;
    const maxLen = Math.max(...chartData.map(c => c.name.length));
    return Math.min(Math.max(maxLen * 6.5, 120), 220);
  }, [chartData]);

  return (
    <div className="cv-tab">

      {/* ── Controles ─────────────────────────────────────────────────────── */}
      <Card className="chart-card mb-3">
        <CardBody className="py-3">
          <div className="cv-controls">
            {/* Rango de fechas (DateRangeFilter canónico) */}
            <div className="cv-controls__group">
              <span className="cv-controls__label">{t('Período')}</span>
              <DateRangeFilter
                value={activePreset}
                onChange={(s, e, p) => { setStartDate(s); setEndDate(e); setActivePreset(p); }}
                presets={CV_PRESETS}
              />
            </div>

            {/* Separador */}
            <div className="cv-controls__divider" />

            {/* Grupo */}
            <div className="cv-controls__group cv-controls__group--field">
              <Label for="cv-group" className="cv-controls__label">{t('Clientes')}</Label>
              <Input
                id="cv-group"
                type="select"
                value={group}
                onChange={e => setGroup(e.target.value)}
                className="cv-date-input"
              >
                {GROUP_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{t(o.labelKey)}</option>
                ))}
              </Input>
            </div>

            {/* Botón */}
            <div className="cv-controls__group cv-controls__group--action">
              <span className="cv-controls__label">&nbsp;</span>
              <button
                className="cv-consult-btn"
                onClick={fetchData}
                disabled={loading}
              >
                <i className={`fas ${loading ? 'fa-sync-alt spinning' : 'fa-search'}`} />
                {loading ? t('Cargando…') : t('Consultar')}
              </button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Estado inicial / error ─────────────────────────────────────────── */}
      {!hasLoaded && !loading && !fetchError && (
        <div className="cv-empty-state">
          <i className="fas fa-chart-bar" />
          <p>{t('Selecciona un rango de fechas y haz clic en')} <strong>{t('Consultar')}</strong> {t('para ver el volumen por cliente.')}</p>
        </div>
      )}
      {fetchError && !loading && (
        <ErrorState message={fetchError.message} onRetry={fetchData} />
      )}

      {/* ── Contenido ─────────────────────────────────────────────────────── */}
      {(hasLoaded || loading) && (
        <>
          {/* KPI cards */}
          <Row className="mb-3 g-3">
            {[
              { title: t('Total Creados'),        value: totals.total,   subtitle: `${startDate} → ${endDate}`, color: '#3B82F6', icon: 'fas fa-box'           },
              { title: t('Gestionados'),          value: totals.managed, subtitle: `${managedPct}% ${t('del total')}`,  color: '#10B981', icon: 'fas fa-check-circle'   },
              { title: t('Pendientes de Gestión'),value: totals.pending, subtitle: `${pendingPct}% ${t('del total')}`,  color: '#EF4444', icon: 'fas fa-hourglass-half' },
            ].map(kpi => (
              <Col key={kpi.title} xs="12" md="4">
                <KpiCard {...kpi} value={kpi.value.toLocaleString()} loading={loading} />
              </Col>
            ))}
          </Row>

          {/* Gráfico stacked horizontal */}
          <Card className="chart-card mb-3">
            <CardHeader className="chart-card__header d-flex align-items-center justify-content-between">
              <h4 className="chart-card__title mb-0">
                {t('Volumen por Cliente')}
                {group !== 'all' && (
                  <Badge tone="brand" className="ms-2 align-middle">
                    {t(GROUP_OPTIONS.find(o => o.value === group)?.labelKey)}
                  </Badge>
                )}
              </h4>
              {!loading && filteredClients.length > 0 && (
                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                  {filteredClients.length} {t('cliente')}{filteredClients.length !== 1 ? 's' : ''}
                </span>
              )}
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="skeleton-shimmer" style={{ height: 320, borderRadius: 8 }} />
              ) : filteredClients.length === 0 ? (
                <div className="cv-empty-state">
                  <i className="fas fa-inbox" />
                  <p>{t('No hay datos para el rango seleccionado.')}</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 4, right: 48, left: 0, bottom: 4 }}
                    barSize={20}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridColor} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: tickColor }} tickLine={false} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={yAxisWidth}
                      tick={{ fontSize: 11, fill: tickColor }}
                      tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend formatter={val => val === 'managed' ? t('Gestionados') : t('Pendientes')} wrapperStyle={{ color: tickColor }} />
                    <Bar dataKey="managed" name="managed" stackId="a" fill={COLUMN_COLORS.managed} />
                    <Bar dataKey="pending" name="pending" stackId="a" fill={COLUMN_COLORS.pending} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardBody>
          </Card>

          {/* Tabla detalle */}
          {!loading && filteredClients.length > 0 && (
            <Card className="chart-card">
              <CardHeader className="chart-card__header">
                <h4 className="chart-card__title mb-0">{t('Detalle por Cliente')}</h4>
              </CardHeader>
              <CardBody className="p-0 cv-tblwrap">
                <UiTable>
                  <THead>
                    <TR>
                      <TH className="w-10">#</TH>
                      <SortableTH col="businessName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                        {t('Cliente')}
                      </SortableTH>
                      <SortableTH col="total" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Total')}
                      </SortableTH>
                      <SortableTH col="managed" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Gestionados')}
                      </SortableTH>
                      <SortableTH col="pending" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">
                        {t('Pendientes')}
                      </SortableTH>
                      <TH className="text-right">% {t('Gestión')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {filteredClients.map((c, i) => {
                      const pct    = c.total > 0 ? ((c.managed / c.total) * 100).toFixed(1) : '0.0';
                      const pctNum = parseFloat(pct);
                      return (
                        <TR key={c.companyId || i}>
                          <TD className="align-middle text-fg-subtle">{i + 1}</TD>
                          <TD className="align-middle font-semibold max-w-[260px] truncate" title={c.businessName}>
                            {c.businessName}
                          </TD>
                          <TD className="align-middle text-right">{c.total.toLocaleString()}</TD>
                          <TD className="align-middle text-right">{c.managed.toLocaleString()}</TD>
                          <TD className={`align-middle text-right ${c.pending > 0 ? 'text-status-error font-semibold' : 'text-fg-subtle'}`}>
                            {c.pending.toLocaleString()}
                          </TD>
                          <TD className="align-middle text-right">
                            <Badge
                              tone={pctNum >= 80 ? 'success' : pctNum >= 50 ? 'warning' : 'error'}
                              className="min-w-[52px] justify-center"
                            >
                              {pct}%
                            </Badge>
                          </TD>
                        </TR>
                      );
                    })}
                  </TBody>
                </UiTable>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

// memo: no recibe props; lo aísla de los re-renders del dashboard padre.
export default React.memo(ClientVolumeTab);
